import React, { useState, useRef, useEffect } from 'react';
import { FaCheck, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const MultiSelect = ({ 
  options = [], 
  selectedValues = [], 
  onChange, 
  placeholder = "Select options...",
  disabled = false,
  renderOption = (option) => option.label || option.name || option,
  getValue = (option) => option.value || option.id || option,
  getDisplayName = (value) => value, // Function to get display name from value
  maxHeight = "200px"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const containerRef = useRef(null);

  const handleToggle = (value) => {
    const isSelected = selectedValues.includes(value);
    let newSelected;
    
    if (isSelected) {
      newSelected = selectedValues.filter(item => item !== value);
    } else {
      newSelected = [...selectedValues, value];
    }
    
    onChange(newSelected);
  };

  const selectedCount = selectedValues.length;

  // Calculate dropdown position to prevent viewport overflow
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If there's not enough space below, show dropdown above
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Selected Display Area */}
      <div 
        className={`
          w-full p-4 border rounded-lg cursor-pointer transition-all duration-200 select-none
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50 active:bg-gray-100'}
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200 shadow-md' : 'border-gray-300 hover:border-gray-400'}
          flex items-center justify-between min-h-[56px] text-base
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex-1">
          {selectedCount === 0 ? (
            <span className="text-gray-500">{placeholder}</span>
          ) : (
            <div className="flex items-center">
              <span className="text-gray-900 font-medium">
                {selectedCount} selected
              </span>
              {selectedCount <= 2 && (
                <span className="text-sm text-gray-600 ml-2 truncate">
                  ({selectedValues.slice(0, 2).map(value => {
                    const displayName = getDisplayName(value);
                    return displayName.length > 20 ? displayName.substring(0, 20) + '...' : displayName;
                  }).join(', ')}{selectedCount > 2 ? '...' : ''})
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-gray-400">
          {isOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && !disabled && (
        <div 
          className={`absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          style={{ maxHeight: maxHeight }}
        >
          <div className="overflow-y-auto" style={{ maxHeight: `calc(${maxHeight} - 4px)`, height: `calc(${maxHeight} - 4px)` }}>
            {options.length === 0 ? (
              <div className="p-3 text-gray-500 text-center">No options available</div>
            ) : (
              options.map((option, index) => {
                const value = getValue(option);
                const isSelected = selectedValues.includes(value);
                
                return (
                  <div
                    key={index}
                    className={`
                      p-4 cursor-pointer transition-all duration-150 flex items-center justify-between min-h-[60px]
                      hover:bg-blue-50 border-b border-gray-100 last:border-b-0 active:bg-blue-200 select-none
                      ${isSelected ? 'bg-blue-100 text-blue-900 border-blue-200' : 'text-gray-700'}
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(value);
                    }}
                  >
                    <div className="flex-1 pr-2 min-w-0">
                      {renderOption(option)}
                    </div>
                    {isSelected && (
                      <div className="text-white bg-blue-600 rounded-full p-1 flex items-center justify-center min-w-[24px] min-h-[24px]">
                        <FaCheck size={12} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default MultiSelect; 